import { request } from './http'
import { skeletonApi, type SkeletonJobStatus } from './skeletonApi'

export type AutopilotScope = 'volume' | 'book'
export type AutopilotStatus =
  | 'planning'
  | 'running'
  | 'paused'
  | 'blocked_plan'
  | 'blocked_credits'
  | 'completed'
  | 'failed'
  | 'cancelled'
// 与 backend/db/migration/141 的 CHECK 约束保持一致。
// needs_review / job_failed 是 141 从 quality 里拆出来的 —— quality 现在只表示"连续低分"。
export type AutopilotPauseReason =
  | '' | 'checkpoint' | 'credits' | 'quality'
  | 'needs_review' | 'job_failed' | 'budget'
  | 'user' | 'invariant_violation' | 'andon'

export interface AutopilotEstimateInput {
  target_words: number
  chapter_word_target?: number
  genre: string
  scope: AutopilotScope
}

export interface AutopilotEstimate {
  total_chapters: number
  volume_count: number
  first_volume_chapters: number
  scoped_chapters: number
  scoped_words: number
  credits_low: number
  credits_high: number
}

export interface AutopilotStartInput {
  scope?: AutopilotScope
  target_volume_no?: number
  branch_id?: string
  quality_threshold?: number
  quality_max_consecutive_low?: number
}

export interface AutopilotRun {
  pending_entity_reviews?: Array<{ id: string; canonical_name: string; chapter_no: number }>
  id: string
  project_id: string
  branch_id: string
  scope: AutopilotScope
  target_volume_no: number | null
  status: AutopilotStatus
  pause_reason: AutopilotPauseReason
  /** 暂停的具体原因(来自 autopilot_step.error); pause_reason 说明类别, 这里说明具体发生了什么。 */
  pause_detail: string
  cursor_chapter_no: number
  start_chapter_no: number
  end_chapter_no: number
  credits_estimated: number
  credits_spent: number
  quality_threshold: number
  quality_consecutive_low: number
  next_checkpoint_chapter_no: number
  last_chapter_quality: number
  created_at: number
  updated_at: number
  finished_at: number
}

export interface SkeletonJobPollOptions {
  intervalMs?: number
  signal?: AbortSignal
}

function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason)
      return
    }
    const timer = window.setTimeout(resolve, ms)
    signal?.addEventListener('abort', () => {
      window.clearTimeout(timer)
      reject(signal.reason)
    }, { once: true })
  })
}

export async function waitForSkeletonJob(
  projectId: string,
  jobId: string,
  options: SkeletonJobPollOptions = {},
): Promise<SkeletonJobStatus> {
  const intervalMs = options.intervalMs ?? 2000
  for (;;) {
    const job = await skeletonApi.job(projectId, jobId)
    if (job.status === 'done') return job
    if (job.status === 'failed') throw new Error(job.error || 'skeleton job failed')
    await wait(intervalMs, options.signal)
  }
}

function projectRunPath(projectId: string, runId?: string): string {
  const base = `/v1/projects/${encodeURIComponent(projectId)}/autopilot`
  return runId ? `${base}/${encodeURIComponent(runId)}` : base
}

export const autopilotApi = {
  estimate: (body: AutopilotEstimateInput): Promise<AutopilotEstimate> =>
    request<AutopilotEstimate>('/v1/autopilot/estimate', { method: 'POST', body }),

  start: (projectId: string, body: AutopilotStartInput): Promise<AutopilotRun> =>
    request<AutopilotRun>(projectRunPath(projectId), { method: 'POST', body }),

  getActive: async (projectId: string, branchId?: string): Promise<AutopilotRun | null> =>
    (await request<AutopilotRun | undefined>(projectRunPath(projectId), { branchId })) ?? null,

  get: (projectId: string, runId: string): Promise<AutopilotRun> =>
    request<AutopilotRun>(projectRunPath(projectId, runId)),

  pause: (projectId: string, runId: string): Promise<AutopilotRun> =>
    request<AutopilotRun>(`${projectRunPath(projectId, runId)}/pause`, { method: 'POST' }),

  resume: (projectId: string, runId: string): Promise<AutopilotRun> =>
    request<AutopilotRun>(`${projectRunPath(projectId, runId)}/resume`, { method: 'POST' }),

  cancel: (projectId: string, runId: string): Promise<AutopilotRun> =>
    request<AutopilotRun>(`${projectRunPath(projectId, runId)}/cancel`, { method: 'POST' }),
}
